import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { getConfiguredBot } from '@/config'
import { authSession, type AnonymousSessionClaimTarget } from '@/auth/session'
import {
  ApiError,
  chatClient,
  fetchBotIntro,
  getSseStreamingClient,
  mockStreamingClient,
  type ArtifactPart,
  type Bot,
  type BotIntro,
  type ChatStatus,
  type ConversationPersistence,
  type FilePart,
  type Message,
  type MessagePart,
  type Session,
  type StreamEvent,
  type StreamingChatClient,
  type OAuthConnectorStatus,
} from '@/services'
import { CHAT_ERROR_RETRY_TEMPLATE_ID } from '@/services/messageTemplates'
import { createLatestRequestManager, isAbortError } from '../lib/requestCancellation'
import { chatTrace } from '../lib/chatTrace'
import { useChatHistoryStore } from './chatHistoryStore'

export type ConversationClientType = 'mock' | 'sse'

interface ActiveStreamTurn {
  userMessageId: string
  assistantMessageId: string
}

interface ComposerDraft {
  content: string
  requestedAt: number
}

interface ConnectorAuthorizationProgress {
  connectorName: string
  stage: 'authorizing' | 'refreshing' | 'retrying'
}

interface StreamErrorPayload {
  message?: string
  code?: string
  status?: number
  templateId?: string
  templateData?: Record<string, unknown>
}

interface StreamExecutionRequest {
  kind?: 'text' | 'structured'
  content: string
  files: FilePart[]
  botId: string
  sessionId: string | null
  userMessageId: string
  assistantMessageId: string
  bot: Bot
  replaceLastTurn?: boolean
  correlationId?: string
  actionId?: string
  structuredPayload?: Record<string, unknown>
}

function buildPendingSessionId(): string {
  return `pending-${crypto.randomUUID()}`
}

function buildTextPart(content: string): MessagePart {
  return { type: 'text', content, format: 'markdown' }
}

function formatStructuredSubmissionLabel(actionId: string): string {
  const words = actionId.replace(/[_-]+/g, ' ').trim()
  if (!words) return 'Submitted response'
  if (words.startsWith('submit ')) return `Submitted ${words.slice('submit '.length)}`
  if (words.startsWith('select ')) return `Selected ${words.slice('select '.length)}`
  return `Submitted ${words}`
}

function extractUserTurnPayload(parts: MessagePart[]): { content: string; files: FilePart[] } {
  const content = parts
    .filter((part): part is { type: 'text'; content: string } => part.type === 'text')
    .map(part => part.content)
    .join('\n\n')
  const files = parts.flatMap((part): FilePart[] => {
    if (part.type === 'file') {
      return [{ ...part }]
    }
    if (
      part.type === 'artifact' &&
      'source' in part &&
      part.source === 'user' &&
      (part.artifactType === 'file' || part.artifactType === 'text') &&
      typeof part.data.file_id === 'string' &&
      typeof part.data.name === 'string' &&
      typeof part.data.mime_type === 'string'
    ) {
      return [{
        type: 'file',
        fileId: part.data.file_id,
        name: part.data.name,
        mimeType: part.data.mime_type,
        size: part.data.size ?? 0,
        purpose: 'user-upload',
      }]
    }
    return []
  })
  return { content, files }
}

function replaceMessageIds(
  items: Message[],
  {
    previousUserMessageId,
    previousAssistantMessageId,
    nextUserMessageId,
    nextAssistantMessageId,
  }: {
    previousUserMessageId: string
    previousAssistantMessageId: string
    nextUserMessageId: string
    nextAssistantMessageId: string
  },
): Message[] {
  return items.map((message) => {
    if (message.id === previousUserMessageId) {
      return { ...message, id: nextUserMessageId }
    }
    if (message.id === previousAssistantMessageId) {
      return { ...message, id: nextAssistantMessageId }
    }
    return message
  })
}

function mergeBots(existingBots: Bot[], nextBots: Bot[]): Bot[] {
  const merged = new Map(existingBots.map(bot => [bot.id, bot]))
  for (const bot of nextBots) {
    const current = merged.get(bot.id)
    merged.set(bot.id, { ...current, ...bot })
  }
  return Array.from(merged.values())
}

function mapGreetingErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 403) {
      return 'You are signed in, but you do not have access to this assistant.'
    }
  }

  return error instanceof Error ? error.message : 'Failed to fetch greeting'
}

function isAuthorizationTransitionError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 401
}

function durationMs(startedAt: number): number {
  return Math.round((performance.now() - startedAt) * 100) / 100
}

export const useConversationStore = defineStore('conversation', () => {
  const knownBots = ref<Bot[]>([])
  const messages = ref<Message[]>([])
  const activeBotName = ref<string | null>(null)
  const botIdLookup = ref<Record<string, string>>({})
  const activeConversationId = ref<string | null>(null)
  const activeConversationTitle = ref<string | null>(null)
  const activeConversationReadOnly = ref(false)
  const activeOAuthConnectors = ref<OAuthConnectorStatus[]>([])
  const connectorAuthorizationProgress = ref<ConnectorAuthorizationProgress | null>(null)
  const loadingConversation = ref(false)
  const thinking = ref(false)
  const streamingStatus = ref<ChatStatus>('idle')
  const statusMessage = ref<string | null>(null)
  const botIntro = ref<BotIntro | null>(null)
  const botLoadError = ref<string | null>(null)
  const activeToolCall = ref<{ toolName: string; toolCallId?: string; status: string; message?: string } | null>(null)
  const activeToolCalls = ref<{ toolName: string; toolCallId?: string; status: string; message?: string }[]>([])
  const clientType = ref<ConversationClientType>('sse')
  const conversationPersistence = ref<ConversationPersistence>('persistent')
  const activeStreamTurn = ref<ActiveStreamTurn | null>(null)
  const composerDraft = ref<ComposerDraft | null>(null)
  const activeGreetingRequestId = ref(0)
  const activeConversationLoadRequestId = ref(0)

  const activeStreamingClient = computed<StreamingChatClient>(() =>
    clientType.value === 'mock' ? mockStreamingClient : getSseStreamingClient()
  )

  const activeApiBotId = computed(() => (activeBotName.value ? resolveBotId(activeBotName.value) : null))
  const currentBot = computed(() =>
    activeApiBotId.value ? knownBots.value.find(bot => bot.id === activeApiBotId.value) : undefined,
  )

  let streamingInitialized: ConversationClientType | null = null
  let finalizeActiveStream: (() => void) | null = null
  const requestManager = createLatestRequestManager()
  const conversationLoadRequestKey = 'conversation-load'
  const greetingRequestKey = 'greeting-load'

  function clearStreamingUiState() {
    statusMessage.value = null
    thinking.value = false
    activeToolCall.value = null
    activeToolCalls.value = []
  }

  function updateActiveSessionState(session: Session) {
    activeConversationTitle.value = session.title
    activeConversationReadOnly.value = session.readOnly ?? false
    activeOAuthConnectors.value = session.oauthConnectors ?? []
  }

  function beginConnectorAuthorization(connectorName: string) {
    connectorAuthorizationProgress.value = {
      connectorName,
      stage: 'authorizing',
    }
  }

  function setConnectorAuthorizationStage(stage: ConnectorAuthorizationProgress['stage']) {
    if (!connectorAuthorizationProgress.value) return
    connectorAuthorizationProgress.value = {
      ...connectorAuthorizationProgress.value,
      stage,
    }
  }

  function clearConnectorAuthorizationProgress() {
    connectorAuthorizationProgress.value = null
  }

  function cancelActiveStream() {
    if (!finalizeActiveStream) return
    activeStreamingClient.value.disconnect()
    finalizeActiveStream()
  }

  function cancelMessage() {
    const pendingTurn = activeStreamTurn.value
    const pendingUserMessage = pendingTurn ? getMessageById(pendingTurn.userMessageId) : null
    cancelActiveStream()
    if (!pendingTurn) return
    messages.value = messages.value.filter(
      message => message.id !== pendingTurn.userMessageId && message.id !== pendingTurn.assistantMessageId,
    )
    if (pendingUserMessage) {
      composerDraft.value = {
        content: extractUserTurnPayload(pendingUserMessage.parts).content,
        requestedAt: Date.now(),
      }
    }
    activeStreamTurn.value = null
  }

  async function initStreaming() {
    const client = activeStreamingClient.value
    if (streamingInitialized === clientType.value && streamingStatus.value !== 'disconnected') return
    streamingInitialized = clientType.value

    client.onStatusChange((status) => {
      streamingStatus.value = status
    })

    await client.connect()
  }

  async function reconnectStreaming() {
    streamingInitialized = null
    await initStreaming()
  }

  function registerBots(bots: Bot[]) {
    knownBots.value = mergeBots(knownBots.value, bots)
  }

  function registerBot(bot: Bot) {
    registerBots([bot])
  }

  function setConversationPersistence(persistence: ConversationPersistence) {
    conversationPersistence.value = persistence
  }

  function setActiveBotName(botName: string | null) {
    activeBotName.value = botName
  }

  function setBotIdMapping(routeBotName: string, apiBotId: string) {
    botIdLookup.value = {
      ...botIdLookup.value,
      [routeBotName]: apiBotId,
    }
  }

  function resolveBotId(botName: string): string {
    return botIdLookup.value[botName] ?? botName
  }

  function resolveBotMetadata(botName: string): Bot {
    const apiBotId = resolveBotId(botName)
    const configuredBot = getConfiguredBot(botName)
    if (configuredBot) {
      return {
        id: apiBotId,
        name: configuredBot.name,
        type: configuredBot.type,
      }
    }

    const knownBot = knownBots.value.find(bot => bot.id === apiBotId || bot.id === botName)
    if (knownBot) {
      return knownBot
    }

    const cachedBot = chatClient.getBot(apiBotId)
    if (cachedBot) {
      return cachedBot
    }

    return {
      id: apiBotId,
      name: botName,
      type: 'departmental',
    }
  }

  function findMessageIndex(messageId: string): number {
    return messages.value.findIndex(message => message.id === messageId)
  }

  function getMessageById(messageId: string): Message | null {
    const index = findMessageIndex(messageId)
    return index === -1 ? null : messages.value[index] ?? null
  }

  function updateMessageById(messageId: string, updater: (message: Message) => Message): boolean {
    const index = findMessageIndex(messageId)
    if (index === -1) return false
    const current = messages.value[index]
    if (!current) return false
    const nextMessages = [...messages.value]
    nextMessages[index] = updater(current)
    messages.value = nextMessages
    return true
  }

  function setAssistantError(
    assistantMessageId: string,
    error: StreamErrorPayload,
  ) {
    const message = error?.message ?? 'Failed to get response. Please try again.'
    const templateId = error?.templateId ?? CHAT_ERROR_RETRY_TEMPLATE_ID
    updateMessageById(assistantMessageId, (assistantMessage) => {
      const parts: MessagePart[] = [buildTextPart(`_Error: ${message}_`)]
      if (templateId) {
        parts.push({
          type: 'adaptive-card',
          templateId,
          data: error.templateData,
        })
      }
      return {
        ...assistantMessage,
        parts,
      }
    })
  }

  function failPendingArtifacts(messageId: string) {
    const message = getMessageById(messageId)
    if (!message) return
    let changed = false
    const nextParts = message.parts.map((part) => {
      if (part.type === 'pending-artifact' && part.status === 'pending') {
        changed = true
        return { ...part, status: 'failed' as const }
      }
      return part
    })
    if (changed) {
      updateMessageById(messageId, current => ({ ...current, parts: nextParts }))
    }
  }

  function syncTurnSession(sessionId: string, messageIds: string[]) {
    if (activeConversationId.value !== sessionId) {
      activeConversationId.value = sessionId
    }
    messages.value = messages.value.map(message =>
      messageIds.includes(message.id)
        ? { ...message, sessionId }
        : message,
    )
  }

  async function executeStreamTurn({
    kind,
    content,
    files,
    botId,
    sessionId,
    userMessageId,
    assistantMessageId,
    bot,
    replaceLastTurn,
    correlationId,
    actionId,
    structuredPayload,
  }: StreamExecutionRequest) {
    await initStreaming()
    const client = activeStreamingClient.value

    thinking.value = true

    return new Promise<void>((resolve) => {
      let finished = false
      let currentUserMessageId = userMessageId
      let currentAssistantMessageId = assistantMessageId

      const cleanup = () => {
        if (finished) return
        finished = true
        if (finalizeActiveStream === cleanup) {
          finalizeActiveStream = null
        }
        if (
          activeStreamTurn.value?.userMessageId === currentUserMessageId &&
          activeStreamTurn.value?.assistantMessageId === currentAssistantMessageId
        ) {
          activeStreamTurn.value = null
        }
        clearStreamingUiState()
        failPendingArtifacts(currentAssistantMessageId)
        resolve()
      }
      finalizeActiveStream = cleanup

      client.onMessage((data: unknown) => {
        if (finished) return
        const event = data as StreamEvent

        if (event.type === 'status') {
          statusMessage.value = event.message
        }

        if (event.type === 'chunk' && event.content) {
          statusMessage.value = null
          updateMessageById(currentAssistantMessageId, (assistantMessage) => {
            const textPartIndex = assistantMessage.parts.findIndex(part => part.type === 'text')
            const textPart = assistantMessage.parts[textPartIndex]
            if (textPart?.type !== 'text') return assistantMessage
            const parts = [...assistantMessage.parts]
            parts[textPartIndex] = { ...textPart, content: textPart.content + event.content }
            return {
              ...assistantMessage,
              parts,
            }
          })
        }

        if (event.type === 'sources_used') {
          updateMessageById(currentAssistantMessageId, assistantMessage => ({
            ...assistantMessage,
            sources: event.sources,
          }))
        }

        if (event.type === 'tool_status') {
          if (event.status === 'started') {
            const toolCall = {
              toolName: event.toolName,
              toolCallId: event.toolCallId,
              status: event.status,
              message: event.message,
            }
            activeToolCalls.value = [
              ...activeToolCalls.value.filter(tool =>
                event.toolCallId ? tool.toolCallId !== event.toolCallId : tool.toolName !== event.toolName,
              ),
              toolCall,
            ]
            activeToolCall.value ??= toolCall
            if (event.createsArtifact) {
              updateMessageById(currentAssistantMessageId, assistantMessage => ({
                ...assistantMessage,
                parts: [
                  ...assistantMessage.parts,
                  {
                    type: 'pending-artifact',
                    toolName: event.toolName,
                    toolCallId: event.toolCallId,
                    status: 'pending',
                  },
                ],
              }))
            }
          } else if (event.status === 'failed') {
            updateMessageById(currentAssistantMessageId, assistantMessage => ({
              ...assistantMessage,
              parts: assistantMessage.parts.map((part) => {
                if (
                  part.type === 'pending-artifact' &&
                  (event.toolCallId ? part.toolCallId === event.toolCallId : part.toolName === event.toolName)
                ) {
                  return { ...part, status: 'failed' as const }
                }
                return part
              }),
            }))
            activeToolCalls.value = activeToolCalls.value.filter(tool =>
              event.toolCallId ? tool.toolCallId !== event.toolCallId : tool.toolName !== event.toolName,
            )
            activeToolCall.value = activeToolCalls.value[0] ?? null
          } else {
            activeToolCalls.value = activeToolCalls.value.filter(tool =>
              event.toolCallId ? tool.toolCallId !== event.toolCallId : tool.toolName !== event.toolName,
            )
            activeToolCall.value = activeToolCalls.value[0] ?? null
          }
        }

        if (event.type === 'artifact') {
          const targetMessageId = event.messageId ?? (event.messageRole === 'user' ? currentUserMessageId : currentAssistantMessageId)
          updateMessageById(targetMessageId, (targetMessage) => {
            const artifactPart: ArtifactPart = {
              type: 'artifact',
              artifactType: event.artifactType,
              placement: event.placement,
              source: event.source,
              data: event.data,
            } as ArtifactPart
            const attachmentFileId = event.messageRole === 'user' && event.data && typeof event.data === 'object'
              ? (event.data as { file_id?: unknown }).file_id
              : undefined
            const partsWithoutReplacedUpload = targetMessage.parts.filter(part =>
              !(part.type === 'file' && typeof attachmentFileId === 'string' && part.fileId === attachmentFileId),
            )
            let replacedPendingArtifact = false
            const partsWithoutPending = partsWithoutReplacedUpload.filter(part => {
              if (
                !replacedPendingArtifact &&
                part.type === 'pending-artifact' &&
                (event.toolCallId ? part.toolCallId === event.toolCallId : (!event.toolName || part.toolName === event.toolName))
              ) {
                replacedPendingArtifact = true
                return false
              }
              return true
            })
            const textIndex = partsWithoutPending.findIndex(part => part.type === 'text')
            const shouldPlaceBeforeText = event.messageRole !== 'user' && event.placement === 'before_text' && textIndex !== -1
            const parts = shouldPlaceBeforeText
              ? [
                  ...partsWithoutPending.slice(0, textIndex),
                  artifactPart,
                  ...partsWithoutPending.slice(textIndex),
                ]
              : [...partsWithoutPending, artifactPart]
            return {
              ...targetMessage,
              parts,
            }
          })
        }

        if (event.type === 'session_updated') {
          if (!activeConversationTitle.value || activeConversationId.value !== event.sessionId) {
            activeConversationTitle.value = event.title
          }
          syncTurnSession(event.sessionId, [currentUserMessageId, currentAssistantMessageId])
          if (conversationPersistence.value === 'persistent') {
            useChatHistoryStore().upsertSession({
              id: event.sessionId,
              title: event.title,
              botId,
              botName: bot.name,
              botType: bot.type,
              createdAt: new Date(),
              updatedAt: new Date(),
            })
          }
        }

        if (event.type === 'message_created') {
          messages.value = replaceMessageIds(messages.value, {
            previousUserMessageId: currentUserMessageId,
            previousAssistantMessageId: currentAssistantMessageId,
            nextUserMessageId: event.userMessageId,
            nextAssistantMessageId: event.assistantMessageId,
          })
          if (
            activeStreamTurn.value?.userMessageId === currentUserMessageId &&
            activeStreamTurn.value?.assistantMessageId === currentAssistantMessageId
          ) {
            activeStreamTurn.value = {
              userMessageId: event.userMessageId,
              assistantMessageId: event.assistantMessageId,
            }
          }
          currentUserMessageId = event.userMessageId
          currentAssistantMessageId = event.assistantMessageId
        }

        if (event.type === 'done') {
          const overrideMessage = event.message
          if (typeof overrideMessage === 'string' && overrideMessage.length > 0) {
            updateMessageById(currentAssistantMessageId, (assistantMessage) => {
              const textPartIndex = assistantMessage.parts.findIndex(part => part.type === 'text')
              const textPart = assistantMessage.parts[textPartIndex]
              if (textPart?.type !== 'text') {
                return assistantMessage
              }
              const parts = [...assistantMessage.parts]
              parts[textPartIndex] = { ...textPart, content: overrideMessage }
              return {
                ...assistantMessage,
                parts,
              }
            })
          }
          if (event.sources?.length) {
            updateMessageById(currentAssistantMessageId, assistantMessage => ({
              ...assistantMessage,
              sources: event.sources,
            }))
          }
          cleanup()
        }
      })

      client.onError((error) => {
        if (finished) return
        setAssistantError(currentAssistantMessageId, error as StreamErrorPayload)
        cleanup()
      })

      const streamPayload: Record<string, unknown> = {
        kind: kind ?? 'text',
        botId,
        sessionId,
      }
      if (kind === 'structured') {
        streamPayload.correlationId = correlationId
        streamPayload.actionId = actionId
        streamPayload.payload = structuredPayload ?? {}
      } else {
        streamPayload.content = content
        streamPayload.fileIds = files.map(file => file.fileId)
        if (replaceLastTurn === true) {
          streamPayload.replaceLastTurn = true
        }
      }
      client.send(streamPayload)
    })
  }

  function resetConversation(botName?: string | null) {
    chatTrace('conversationStore', 'resetConversation', {
      botName: botName ?? null,
      activeBotName: activeBotName.value,
      activeConversationId: activeConversationId.value,
    })
    activeConversationLoadRequestId.value += 1
    requestManager.cancel(conversationLoadRequestKey)
    cancelActiveStream()
    activeConversationId.value = null
    activeConversationTitle.value = null
    activeConversationReadOnly.value = false
    activeOAuthConnectors.value = []
    clearConnectorAuthorizationProgress()
    messages.value = []
    activeStreamTurn.value = null
    loadingConversation.value = false
    clearStreamingUiState()
    botLoadError.value = null
    if (botName !== undefined) {
      activeBotName.value = botName
    }
  }

  function clearAllState() {
    activeConversationLoadRequestId.value += 1
    requestManager.cancel(greetingRequestKey)
    requestManager.cancel(conversationLoadRequestKey)
    cancelActiveStream()
    activeBotName.value = null
    botIdLookup.value = {}
    resetConversation(null)
    botIntro.value = null
    activeStreamTurn.value = null
    activeStreamingClient.value.disconnect()
    streamingInitialized = null
    clearConnectorAuthorizationProgress()
  }

  async function loadConversation(conversationId: string, botName: string) {
    const apiBotId = resolveBotId(botName)
    chatTrace('conversationStore', 'loadConversation.start', {
      botId: apiBotId,
      routeBotName: botName,
      conversationId,
    })
    const requestId = activeConversationLoadRequestId.value + 1
    activeConversationLoadRequestId.value = requestId
    cancelActiveStream()
    activeBotName.value = botName
    activeConversationId.value = conversationId
    const knownSession = useChatHistoryStore().getSession(apiBotId, conversationId)
    activeConversationTitle.value = knownSession?.title ?? null
    activeConversationReadOnly.value = knownSession?.readOnly ?? false
    activeOAuthConnectors.value = knownSession?.oauthConnectors ?? []
    activeStreamTurn.value = null
    loadingConversation.value = true
    try {
      const session = await requestManager.run(
        conversationLoadRequestKey,
        signal => chatClient.getSession(apiBotId, conversationId, signal),
      )
      if (requestId !== activeConversationLoadRequestId.value) {
        return
      }
      activeConversationTitle.value = session.title
      activeConversationReadOnly.value = session.readOnly ?? false
      activeOAuthConnectors.value = session.oauthConnectors ?? []
      const sessionMessages = await requestManager.run(
        conversationLoadRequestKey,
        signal => chatClient.getMessages(apiBotId, conversationId, signal),
      )
      if (requestId !== activeConversationLoadRequestId.value) {
        chatTrace('conversationStore', 'loadConversation.staleResponse', {
          botId: apiBotId,
          routeBotName: botName,
          conversationId,
          requestId,
          activeRequestId: activeConversationLoadRequestId.value,
        })
        return
      }
      messages.value = sessionMessages
      chatTrace('conversationStore', 'loadConversation.success', {
        botId: apiBotId,
        routeBotName: botName,
        conversationId,
        messageCount: sessionMessages.length,
      })
    } catch (err) {
      if (!isAbortError(err)) {
        chatTrace('conversationStore', 'loadConversation.error', {
          botId: apiBotId,
          routeBotName: botName,
          conversationId,
          errorName: err instanceof Error ? err.name : 'unknown',
        })
        throw err
      }
      chatTrace('conversationStore', 'loadConversation.aborted', {
        botId: apiBotId,
        routeBotName: botName,
        conversationId,
      })
    } finally {
      if (requestId === activeConversationLoadRequestId.value) {
        loadingConversation.value = false
      }
    }
  }

  async function refreshActiveSessionReadOnly(botId: string, sessionId: string) {
    if (!authSession.isAuthenticated) {
      // GET /sessions/{id} requires an authenticated owner regardless of the bot's
      // own auth policy, so anonymous/ephemeral sessions must not call it — doing so
      // 401s and trips the global auth-redirect interceptor right after a turn completes.
      return
    }
    try {
      const session = await chatClient.getSession(botId, sessionId)
      if (activeConversationId.value === sessionId) {
        activeConversationReadOnly.value = session.readOnly ?? false
        activeOAuthConnectors.value = session.oauthConnectors ?? []
      }
    } catch {
      // ignore refresh failures; normal error paths already surface during turn operations
    }
  }

  async function startConversation(botName: string): Promise<Session> {
    const apiBotId = resolveBotId(botName)
    const botMetadata = resolveBotMetadata(botName)
    activeBotName.value = botName
    const { session } = await chatClient.greet(apiBotId)
    setBotIdMapping(botName, session.botId)
    activeConversationId.value = session.id
    updateActiveSessionState(session)
    messages.value = []
    registerBot({
      id: session.botId,
      name: session.botName ?? botMetadata.name,
      type: session.botType ?? botMetadata.type,
    })
    return session
  }

  async function ensureActiveSessionForBotId(apiBotId: string): Promise<Session> {
    if (activeConversationId.value && !activeConversationId.value.startsWith('pending-')) {
      const currentSession = await chatClient.getSession(apiBotId, activeConversationId.value)
      updateActiveSessionState(currentSession)
      return currentSession
    }
    const session = await chatClient.createSession(apiBotId)
    activeConversationId.value = session.id
    updateActiveSessionState(session)
    if (activeBotName.value) {
      setBotIdMapping(activeBotName.value, session.botId)
    }
    registerBot({
      id: session.botId,
      name: session.botName ?? resolveBotMetadata(activeBotName.value ?? session.botId).name,
      type: session.botType ?? resolveBotMetadata(activeBotName.value ?? session.botId).type,
    })
    return session
  }

  function getActiveAnonymousSessionClaimTarget(): AnonymousSessionClaimTarget | null {
    const botId = activeApiBotId.value
    const botName = activeBotName.value
    const sessionId = activeConversationId.value
    if (!botId || !botName || !sessionId || sessionId.startsWith('pending-')) return null
    return { botId, botName, sessionId }
  }

  async function claimAnonymousSession(target: AnonymousSessionClaimTarget): Promise<Session> {
    const claimedSession = await chatClient.claimAnonymousSession(target.botId, target.sessionId)
    updateActiveSessionState(claimedSession)
    useChatHistoryStore().upsertSession(claimedSession)
    if (activeConversationId.value !== target.sessionId || activeBotName.value !== target.botName) {
      await loadConversation(target.sessionId, target.botName)
    }
    return claimedSession
  }

  async function sendMessage(
    content: string,
    files: FilePart[] = [],
    botName: string,
    options?: { forceSessionId?: string | null },
  ) {
    if (activeConversationReadOnly.value === true) {
      throw new Error('This session is read-only')
    }
    const apiBotId = resolveBotId(botName)
    chatTrace('conversationStore', 'sendMessage.start', {
      botId: apiBotId,
      routeBotName: botName,
      activeConversationId: activeConversationId.value,
      contentLength: content.length,
      fileCount: files.length,
    })
    cancelActiveStream()
    composerDraft.value = null
    const targetBot = resolveBotMetadata(botName)
    const localSessionId = options?.forceSessionId ?? activeConversationId.value ?? buildPendingSessionId()

    const parts: MessagePart[] = []
    if (content) {
      parts.push(buildTextPart(content))
    }
    parts.push(...files)

    const userMsg: Message = {
      id: crypto.randomUUID(),
      sessionId: localSessionId,
      role: 'user',
      parts,
      createdAt: new Date(),
    }
    messages.value = [...messages.value, userMsg]

    const assistantMsg: Message = {
      id: crypto.randomUUID(),
      sessionId: localSessionId,
      role: 'assistant',
      parts: [buildTextPart('')],
      createdAt: new Date(),
    }
    messages.value = [...messages.value, assistantMsg]

    activeStreamTurn.value = {
      userMessageId: userMsg.id,
      assistantMessageId: assistantMsg.id,
    }

    await executeStreamTurn({
      kind: 'text',
      content,
      files,
      botId: apiBotId,
      sessionId: options?.forceSessionId ?? activeConversationId.value,
      userMessageId: userMsg.id,
      assistantMessageId: assistantMsg.id,
      bot: targetBot,
    })
    if (activeConversationId.value) {
      await refreshActiveSessionReadOnly(apiBotId, activeConversationId.value)
    }
  }

  async function retryLastTurn() {
    cancelActiveStream()
    composerDraft.value = null

    const assistantMessage = [...messages.value].reverse().find(message => message.role === 'assistant')
    if (!assistantMessage) {
      throw new Error('No prior turn is available to retry')
    }
    const assistantIndex = messages.value.findIndex(message => message.id === assistantMessage.id)
    const userMessage = [...messages.value.slice(0, assistantIndex)]
      .reverse()
      .find(message => message.role === 'user')
    if (!userMessage) {
      throw new Error('No prior turn is available to retry')
    }
    const { content, files } = extractUserTurnPayload(userMessage.parts)
    const botName = activeBotName.value
    if (!botName) {
      throw new Error('No active bot is selected')
    }
    const assistantMessageId = assistantMessage.id
    const userMessageId = userMessage.id
    const apiBotId = resolveBotId(botName)
    const retrySessionId = assistantMessage.sessionId ?? userMessage.sessionId
    messages.value = messages.value.map((message) => {
      if (message.id === userMessageId) {
        return {
          ...message,
          sessionId: retrySessionId,
          parts: userMessage.parts.slice(),
        }
      }
      if (message.id === assistantMessageId) {
        return {
          ...message,
          sessionId: retrySessionId,
          parts: [buildTextPart('')],
          sources: [],
        }
      }
      return message
    })
    activeStreamTurn.value = { userMessageId, assistantMessageId }

    return executeStreamTurn({
      kind: 'text',
      content,
      files,
      botId: apiBotId,
      sessionId: retrySessionId,
      userMessageId,
      assistantMessageId,
      bot: resolveBotMetadata(botName),
      replaceLastTurn: true,
    })
  }

  async function submitStructuredInput(params: {
    correlationId: string
    actionId: string
    payload: Record<string, unknown>
  }) {
    const routeBotName = activeBotName.value
    if (!routeBotName) throw new Error('No active bot is selected')
    const sessionId = activeConversationId.value
    if (!sessionId) throw new Error('No active session is selected')
    if (activeConversationReadOnly.value === true) throw new Error('This session is read-only')
    const apiBotId = resolveBotId(routeBotName)
    const targetBot = resolveBotMetadata(routeBotName)

    cancelActiveStream()
    composerDraft.value = null

    const userMsg: Message = {
      id: crypto.randomUUID(),
      sessionId,
      role: 'user',
      parts: [buildTextPart(formatStructuredSubmissionLabel(params.actionId))],
      createdAt: new Date(),
    }
    messages.value = [...messages.value, userMsg]

    const assistantMsg: Message = {
      id: crypto.randomUUID(),
      sessionId,
      role: 'assistant',
      parts: [buildTextPart('')],
      createdAt: new Date(),
    }
    messages.value = [...messages.value, assistantMsg]

    await executeStreamTurn({
      kind: 'structured',
      content: '',
      files: [],
      botId: apiBotId,
      sessionId,
      userMessageId: userMsg.id,
      assistantMessageId: assistantMsg.id,
      bot: targetBot,
      correlationId: params.correlationId,
      actionId: params.actionId,
      structuredPayload: params.payload,
    })
    await refreshActiveSessionReadOnly(apiBotId, sessionId)
  }

  function setClientType(type: ConversationClientType) {
    if (type !== clientType.value) {
      cancelActiveStream()
      activeStreamingClient.value.disconnect()
      clientType.value = type
      streamingInitialized = null
    }
  }

  async function fetchBot(botName: string) {
    const startedAt = performance.now()
    const requestId = activeGreetingRequestId.value + 1
    activeGreetingRequestId.value = requestId
    const botMetadata = resolveBotMetadata(botName)
    const expectedBotId = resolveBotId(botName)
    const hasMatchingIntro = botIntro.value?.botId === expectedBotId
    chatTrace('conversationStore', 'fetchBot.start', {
      botToken: botName,
      expectedBotId,
      requestId,
      hasMatchingIntro,
    })

    botLoadError.value = null
    if (!hasMatchingIntro) {
      botIntro.value = null
    }
    try {
      const intro = await requestManager.run(
        greetingRequestKey,
        signal => fetchBotIntro(botName, undefined, signal),
      )
      setBotIdMapping(botName, intro.botId)
      registerBot({
        id: intro.botId,
        name: intro.name,
        type: intro.botType ?? botMetadata.type,
      })
      if (requestId !== activeGreetingRequestId.value || activeBotName.value !== botName) {
        chatTrace('conversationStore', 'fetchBot.staleResponse', {
          botToken: botName,
          expectedBotId,
          resolvedBotId: intro.botId,
          requestId,
          activeGreetingRequestId: activeGreetingRequestId.value,
          activeBotName: activeBotName.value,
          durationMs: durationMs(startedAt),
        })
        return intro
      }
      botIntro.value = intro
      chatTrace('conversationStore', 'fetchBot.success', {
        botToken: botName,
        expectedBotId,
        resolvedBotId: intro.botId,
        requestId,
        durationMs: durationMs(startedAt),
      })
      return intro
    } catch (err) {
      if (isAbortError(err)) {
        chatTrace('conversationStore', 'fetchBot.aborted', {
          botToken: botName,
          expectedBotId,
          requestId,
          durationMs: durationMs(startedAt),
        })
        return
      }
      if (requestId !== activeGreetingRequestId.value || activeBotName.value !== botName) {
        chatTrace('conversationStore', 'fetchBot.staleError', {
          botToken: botName,
          expectedBotId,
          requestId,
          activeGreetingRequestId: activeGreetingRequestId.value,
          activeBotName: activeBotName.value,
          error: err instanceof Error ? err.message : String(err),
          durationMs: durationMs(startedAt),
        })
        throw err
      }
      if (isAuthorizationTransitionError(err)) {
        botLoadError.value = null
        chatTrace('conversationStore', 'fetchBot.authTransition', {
          botToken: botName,
          expectedBotId,
          requestId,
          status: err.status,
          durationMs: durationMs(startedAt),
        })
        throw err
      }
      botLoadError.value = mapGreetingErrorMessage(err)
      chatTrace('conversationStore', 'fetchBot.error', {
        botToken: botName,
        expectedBotId,
        requestId,
        error: err instanceof Error ? err.message : String(err),
        durationMs: durationMs(startedAt),
      })
      throw err
    }
  }

  return {
    knownBots,
    messages,
    activeBotName,
    activeApiBotId,
    activeConversationId,
    activeConversationTitle,
    activeConversationReadOnly,
    loadingConversation,
    thinking,
    streamingStatus,
    statusMessage,
    botIntro,
    botLoadError,
    activeToolCall,
    activeToolCalls,
    activeOAuthConnectors,
    connectorAuthorizationProgress,
    clientType,
    currentBot,
    conversationPersistence,
    composerDraft,
    registerBots,
    registerBot,
    setConversationPersistence,
    setActiveBotName,
    setBotIdMapping,
    resolveBotId,
    resetConversation,
    clearAllState,
    loadConversation,
    startConversation,
    ensureActiveSessionForBotId,
    getActiveAnonymousSessionClaimTarget,
    claimAnonymousSession,
    sendMessage,
    submitStructuredInput,
    retryLastTurn,
    setClientType,
    reconnectStreaming,
    cancelMessage,
    beginConnectorAuthorization,
    setConnectorAuthorizationStage,
    clearConnectorAuthorizationProgress,
    fetchBot,
  }
})
